import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, CheckCircle, FileText, Trash2, ArrowLeft } from 'lucide-react'
import profileService from '../services/profileService'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import DocumentsRequiredModal from '../components/DocumentsRequiredModal'
import { ACCEPT_STRINGS, ACCEPTED_MIME_TYPES, validateDocumentFile } from '../config/mediaValidation'

const initialDocData = {
  businessRegNumber: '',
  businessRegFile: null as File | null,
  gstNumber: '',
  gstFile: null as File | null,
  addressProofFile: null as File | null,
  yogaAllianceNumber: '',
  yogaAllianceFile: null as File | null,
}

const EditSchoolDocuments: React.FC = () => {
  const navigate = useNavigate()
  const { refreshSchools } = useAuth()
  const [docData, setDocData] = useState(initialDocData)
  const [otherDocuments, setOtherDocuments] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)
  const [showAdminOnboardedHint, setShowAdminOnboardedHint] = useState(false)
  const initialMetaRef = useRef<{ businessRegNumber: string; gstNumber: string; yogaAllianceNumber: string } | null>(null)

  const fileInputClass =
    'block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:cursor-pointer border border-gray-200 rounded-xl bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'
  const textInputClass =
    'w-full px-4 py-2.5 text-sm border border-gray-200 bg-white rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const response = await profileService.getVerificationStatus()
        if (mounted && response.success) {
          const src = response.data?.createdBySource
          const st = response.data?.status || 'PENDING'
          const docCount = response.data?.documents?.length ?? 0
          setShowAdminOnboardedHint(src === 'BOOKMYYOGATTC' && st !== 'VERIFIED' && docCount === 0)
          const m = response.data?.verificationMeta || {}
          initialMetaRef.current = {
            businessRegNumber: (m.businessRegNumber as string) || '',
            gstNumber: (m.gstNumber as string) || '',
            yogaAllianceNumber: (m.yogaAllianceNumber as string) || '',
          }
          setDocData((prev) => ({
            ...prev,
            businessRegNumber: initialMetaRef.current!.businessRegNumber,
            gstNumber: initialMetaRef.current!.gstNumber,
            yogaAllianceNumber: initialMetaRef.current!.yogaAllianceNumber,
          }))
        }
      } catch {
        // keep defaults
      } finally {
        if (mounted) setLoadingStatus(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  const handleDocFile = (key: keyof typeof docData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const validation = validateDocumentFile(file, 'verificationDocument', 0)
    if (validation.valid) {
      setDocData((prev) => ({ ...prev, [key]: file }))
    } else {
      toast.error(validation.error || 'Invalid document')
    }
  }

  const handleDocText = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setDocData((prev) => ({ ...prev, [name]: value }))
  }

  const handleOtherDocuments = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const acceptedTypes = [...ACCEPTED_MIME_TYPES.DOCUMENT] as readonly string[]
    const valid = Array.from(files).filter((f) => acceptedTypes.includes(f.type) || (f.type === '' && /\.(pdf|jpe?g|png|webp)$/i.test(f.name)))
    e.target.value = ''
    if (valid.length === 0) {
      toast.error('Invalid file type. Please upload PDF or images (JPEG, PNG, WebP).')
      return
    }
    setOtherDocuments((prev) => [...prev, ...valid].slice(0, 5))
  }

  const removeOtherDocument = (index: number) => {
    setOtherDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  const getAllDocuments = (): File[] => {
    const list: File[] = []
    if (docData.businessRegFile) list.push(docData.businessRegFile)
    if (docData.gstFile) list.push(docData.gstFile)
    if (docData.addressProofFile) list.push(docData.addressProofFile)
    if (docData.yogaAllianceFile) list.push(docData.yogaAllianceFile)
    return [...list, ...otherDocuments]
  }

  const getDocumentTypes = (): string[] => {
    const types: string[] = []
    if (docData.businessRegFile) types.push('businessReg')
    if (docData.gstFile) types.push('gst')
    if (docData.addressProofFile) types.push('addressProof')
    if (docData.yogaAllianceFile) types.push('yogaAlliance')
    otherDocuments.forEach(() => types.push('additional'))
    return types
  }

  const buildVerificationMeta = (): Record<string, string> => {
    const meta: Record<string, string> = {}
    if (docData.businessRegNumber.trim()) meta.businessRegNumber = docData.businessRegNumber.trim()
    if (docData.gstNumber.trim()) meta.gstNumber = docData.gstNumber.trim()
    if (docData.yogaAllianceNumber.trim()) meta.yogaAllianceNumber = docData.yogaAllianceNumber.trim()
    return meta
  }

  const hasMetaChanged = (): boolean => {
    const init = initialMetaRef.current
    if (!init) return false
    return (
      docData.businessRegNumber.trim() !== init.businessRegNumber.trim() ||
      docData.gstNumber.trim() !== init.gstNumber.trim() ||
      docData.yogaAllianceNumber.trim() !== init.yogaAllianceNumber.trim()
    )
  }

  const canSubmit = getAllDocuments().length > 0 || hasMetaChanged()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const docs = getAllDocuments()
    if (docs.length === 0 && !hasMetaChanged()) {
      setErrors({ _general: 'Upload a document or change registration numbers to update.' })
      return
    }
    setIsLoading(true)
    setErrors({})
    try {
      if (docs.length === 0) {
        await profileService.updateSchoolVerificationMeta({
          businessRegNumber: docData.businessRegNumber.trim(),
          gstNumber: docData.gstNumber.trim(),
          yogaAllianceNumber: docData.yogaAllianceNumber.trim(),
        })
        toast.success('Verification details updated successfully.')
        await refreshSchools().catch(() => {})
        navigate('/profile', { state: { openTab: 'school' } })
        return
      }

      const meta = buildVerificationMeta()
      await profileService.submitVerificationDocuments(
        docs,
        Object.keys(meta).length > 0 ? meta : undefined,
        getDocumentTypes()
      )
      toast.success('Verification documents updated successfully.')
      await refreshSchools().catch(() => {})
      navigate('/profile', { state: { openTab: 'school' } })
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to update documents.'
      toast.error(msg)
      setErrors({ _general: msg })
    } finally {
      setIsLoading(false)
    }
  }

  if (loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Loading current documents...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <button
            type="button"
            onClick={() => navigate('/profile', { state: { openTab: 'school' } })}
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Update verification documents</h1>
            <p className="text-sm text-gray-500 mt-0.5">Edit registration numbers and/or upload new files. You can save number changes without uploading.</p>
          </div>
        </motion.div>

        {showAdminOnboardedHint && (
          <div className="mb-6 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-gray-900">
            <p className="font-semibold text-primary-900">Please upload verification documents</p>
            <p className="mt-1 text-gray-700 leading-relaxed">
              Please upload business registration and address proof (and any optional extras) so we can finish verifying your school.
            </p>
          </div>
        )}

        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="p-6 sm:p-8 space-y-6">
            <button
              type="button"
              onClick={() => setShowDocumentsModal(true)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              What documents do I need?
            </button>
            <DocumentsRequiredModal
              isOpen={showDocumentsModal}
              onClose={() => setShowDocumentsModal(false)}
              checklistIntro="Use this checklist on this page: upload the same kinds of documents you would when registering yourself—business registration, address proof, and optional Yoga Alliance or extras."
            />

            {errors._general && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">{errors._general}</div>
            )}

            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">Business Registration <span className="text-red-500">*</span></legend>
              <p className="text-xs text-gray-500 -mt-1">
                GST registration counts as business registration—upload your GST certificate here if that is your proof, and use GSTIN as the registration number. Skip the GST block below if you already covered it here.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Registration number</label>
                <input type="text" name="businessRegNumber" value={docData.businessRegNumber} onChange={handleDocText} placeholder="e.g. U74999MH2020PTC123456" className={textInputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate (PDF or image)</label>
                <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('businessRegFile')} className={fileInputClass} />
                {docData.businessRegFile && <p className="mt-1 text-sm text-green-600">{docData.businessRegFile.name}</p>}
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">GST Registration <span className="text-gray-500 font-normal">(if applicable)</span></legend>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">GST number</label>
                <input type="text" name="gstNumber" value={docData.gstNumber} onChange={handleDocText} placeholder="e.g. 27AABCU9603R1ZM" className={textInputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload GST certificate</label>
                <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('gstFile')} className={fileInputClass} />
                {docData.gstFile && <p className="mt-1 text-sm text-green-600">{docData.gstFile.name}</p>}
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">Address Proof</legend>
              <p className="text-xs text-gray-500 -mt-1">Utility bill, lease agreement, or property tax receipt</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload document</label>
                <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('addressProofFile')} className={fileInputClass} />
                {docData.addressProofFile && <p className="mt-1 text-sm text-green-600">{docData.addressProofFile.name}</p>}
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">Yoga Alliance Certification <span className="text-gray-500 font-normal">(if certified)</span></legend>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">RYS registration number</label>
                <input type="text" name="yogaAllianceNumber" value={docData.yogaAllianceNumber} onChange={handleDocText} placeholder="e.g. RYS-200-12345" className={textInputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate</label>
                <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('yogaAllianceFile')} className={fileInputClass} />
                {docData.yogaAllianceFile && <p className="mt-1 text-sm text-green-600">{docData.yogaAllianceFile.name}</p>}
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
              <legend className="text-sm font-semibold text-gray-800 px-1">Additional Documents <span className="text-gray-500 font-normal">(optional)</span></legend>
              <p className="text-xs text-gray-500 -mt-1">Insurance, tax certificates, course brochures, etc.</p>
              <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} multiple onChange={handleOtherDocuments} className={fileInputClass} />
              {otherDocuments.length > 0 && (
                <ul className="space-y-1.5">
                  {otherDocuments.map((file, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="truncate flex-1">{file.name}</span>
                      <button type="button" onClick={() => removeOtherDocument(i)} className="text-red-500 hover:text-red-600 shrink-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          </div>

          <div className="px-6 sm:px-8 py-4 bg-gray-50/80 border-t border-gray-100 flex gap-2">
            <button type="button" onClick={() => navigate('/profile', { state: { openTab: 'school' } })} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !canSubmit}
              className="flex-1 flex justify-center items-center gap-1.5 py-2.5 px-4 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 text-white" />
                  Update documents
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  )
}

export default EditSchoolDocuments
